<template>
  <div
    class="modal fade"
    id="item-lifecycle-modal"
    tabindex="-1"
    aria-labelledby="itemLifeCycleModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable lc-dialog">
      <div class="modal-content lc-content">
        <div class="modal-header border-0 lc-header">
          <div class="lc-header-text">
            <p class="lc-eyebrow mb-1">{{ $t('life_cycle') }}</p>
            <h4 id="itemLifeCycleModalLabel" class="mb-0">
              {{ item.item_name || '—' }}
              <span v-if="item.variant" class="lc-variant-label"> ({{ item.variant }})</span>
            </h4>
            <p class="lc-meta mb-0">
              <span v-if="item.item_code">{{ $t('item_code') }}: {{ item.item_code }}</span>
              <span v-if="item.unit" class="lc-dot-sep">·</span>
              <span v-if="item.unit">{{ $t('unit') }}: {{ item.unit }}</span>
              <span v-if="item.category" class="lc-dot-sep">·</span>
              <span v-if="item.category">{{ item.category }}</span>
            </p>
          </div>
          <div class="lc-current-pill" :title="$t('current_quantity')">
            <span class="lc-current-label">{{ $t('current_quantity') }}</span>
            <strong class="lc-current-value">{{ formatQty(currentQty) }}</strong>
          </div>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <div class="modal-body lc-body">
          <div v-if="loading" class="lc-state">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="mb-0 mt-2 text-muted">{{ $t('loading') }}…</p>
          </div>

          <div v-else-if="error" class="lc-state text-danger">
            <i class="ti ti-alert-circle fs-3"></i>
            <p class="mb-0 mt-2">{{ error }}</p>
          </div>

          <template v-else>
            <div class="lc-summary">
              <div
                v-for="chip in summaryChips"
                :key="chip.key"
                class="lc-chip"
                :class="`tone-${chip.tone}`"
              >
                <span class="lc-chip-label">{{ chip.label }}</span>
                <strong class="lc-chip-value">{{ formatQty(chip.value) }}</strong>
              </div>
            </div>

            <div v-if="!events.length" class="lc-empty">
              <i class="ti ti-history"></i>
              <h5>{{ $t('life_cycle_empty') }}</h5>
              <p>{{ $t('life_cycle_empty_hint') }}</p>
            </div>

            <div v-else class="lc-timeline">
              <div
                v-for="(ev, index) in events"
                :key="ev.id"
                class="lc-event"
                :class="[`tone-${ev.tone || 'gray'}`, ev.direction]"
              >
                <div class="lc-rail">
                  <span class="lc-rail-dot">
                    <i :class="eventIcon(ev)"></i>
                  </span>
                  <span v-if="index < events.length - 1" class="lc-rail-line"></span>
                </div>

                <div class="lc-card">
                  <div class="lc-card-top">
                    <div>
                      <span class="lc-type-badge">{{ ev.type_label }}</span>
                      <h6 class="lc-card-title mb-0">
                        <span :class="ev.direction === 'in' ? 'qty-in' : 'qty-out'">
                          {{ ev.direction === 'in' ? '+' : '−' }}{{ formatQty(ev.qty) }}
                        </span>
                        <span class="text-muted fw-normal"> → {{ $t('balance') }} {{ formatQty(ev.balance) }}</span>
                      </h6>
                    </div>
                    <div class="lc-card-voucher text-end">
                      <div class="lc-voucher-id">{{ ev.voucher_id || '—' }}</div>
                      <div class="lc-voucher-date">{{ formatDate(ev.voucher_date) }}</div>
                    </div>
                  </div>

                  <div class="lc-facts">
                    <div class="lc-fact">
                      <i class="ti ti-user"></i>
                      <div>
                        <span class="lc-fact-label">{{ $t('who') }}</span>
                        <span class="lc-fact-value">{{ ev.who || '—' }}</span>
                      </div>
                    </div>
                    <div class="lc-fact">
                      <i class="ti ti-clock"></i>
                      <div>
                        <span class="lc-fact-label">{{ $t('when') }}</span>
                        <span class="lc-fact-value">{{ formatDateTime(ev.when) || formatDate(ev.voucher_date) }}</span>
                      </div>
                    </div>
                    <div class="lc-fact lc-fact-how">
                      <i class="ti ti-info-circle"></i>
                      <div>
                        <span class="lc-fact-label">{{ $t('how') }}</span>
                        <span class="lc-fact-value">
                          <template v-if="ev.how && ev.how.length">
                            <span
                              v-for="(part, i) in ev.how"
                              :key="i"
                              class="lc-how-part"
                            >{{ part }}</span>
                          </template>
                          <template v-else>—</template>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiUrls from '@/urls';

const ICON_MAP = {
  package: 'ti ti-package',
  truck: 'ti ti-truck',
  'shopping-cart': 'ti ti-shopping-cart',
  receipt: 'ti ti-receipt',
  'alert-triangle': 'ti ti-alert-triangle',
  'arrow-down-left': 'ti ti-arrow-down-left',
  'arrow-up-right': 'ti ti-arrow-up-right',
  adjustments: 'ti ti-adjustments',
  'building-factory': 'ti ti-building-factory-2',
  tool: 'ti ti-tool',
  history: 'ti ti-history',
};

export default {
  name: 'ItemLifeCycleModal',
  props: {
    itemId: {
      type: [Number, String],
      default: null,
    },
    variantId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      error: '',
      item: {},
      currentQty: 0,
      summary: {},
      events: [],
    };
  },
  computed: {
    summaryChips() {
      const s = this.summary || {};
      return [
        { key: 'open_stock', label: this.$t('open_stock'), value: s.open_stock, tone: 'teal' },
        { key: 'purchase', label: this.$t('purchase'), value: s.purchase, tone: 'blue' },
        { key: 'sale', label: this.$t('sale'), value: s.sale, tone: 'violet' },
        { key: 'sale_return', label: this.$t('sale_return'), value: s.sale_return, tone: 'amber' },
        { key: 'damage', label: this.$t('damage'), value: s.damage, tone: 'rose' },
        { key: 'purchase_return', label: this.$t('purchase_return'), value: s.purchase_return, tone: 'orange' },
        { key: 'transfer_in', label: this.$t('transfer_in'), value: s.transfer_in, tone: 'cyan' },
        { key: 'transfer_out', label: this.$t('transfer_out'), value: s.transfer_out, tone: 'slate' },
      ].filter((c) => Number(c.value || 0) !== 0 || ['open_stock', 'purchase', 'sale'].includes(c.key));
    },
  },
  watch: {
    itemId: {
      immediate: true,
      handler(id) {
        if (id) this.fetchLifeCycle(id, this.variantId);
      },
    },
    variantId() {
      if (this.itemId) this.fetchLifeCycle(this.itemId, this.variantId);
    },
  },
  methods: {
    eventIcon(ev) {
      return ICON_MAP[ev.icon] || 'ti ti-history';
    },

    formatQty(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return '0';
      if (Number.isInteger(n)) return String(n);
      return String(Math.round(n * 1000) / 1000);
    },

    formatDate(value) {
      if (!value) return '—';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },

    formatDateTime(value) {
      if (!value) return '';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    async fetchLifeCycle(itemId, variantId = null) {
      this.loading = true;
      this.error = '';
      this.item = {};
      this.currentQty = 0;
      this.summary = {};
      this.events = [];
      try {
        const url = apiUrls.item.lifecycle.replace(':item_id', itemId);
        const params = {};
        if (variantId != null && variantId !== '' && variantId !== 0 && variantId !== '0') {
          params.variant_id = variantId;
        }
        const { data } = await this.$axios.get(url, { params });
        this.item = data.item || {};
        this.currentQty = data.current_qty || 0;
        this.summary = data.summary || {};
        this.events = data.events || [];
      } catch (e) {
        this.error = e?.response?.data?.detail || e?.message || this.$t('life_cycle_load_failed');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.lc-dialog {
  max-width: 920px;
}
.lc-content {
  border: 0;
  border-radius: 18px;
  overflow: hidden;
  background:
    radial-gradient(1200px 280px at 0% -10%, rgba(14, 165, 233, 0.12), transparent 55%),
    radial-gradient(900px 260px at 100% 0%, rgba(99, 102, 241, 0.10), transparent 50%),
    #fff;
}
.lc-header {
  align-items: flex-start;
  gap: 1rem;
  padding: 1.35rem 1.5rem 0.75rem;
}
.lc-eyebrow {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  margin: 0;
}
.lc-header-text h4 {
  font-weight: 700;
  color: #0f172a;
}
.lc-variant-label {
  font-weight: 600;
  color: #475569;
  font-size: 0.92em;
}
.lc-meta {
  color: #64748b;
  font-size: 0.86rem;
  margin-top: 0.25rem;
}
.lc-dot-sep {
  margin: 0 0.35rem;
  opacity: 0.6;
}
.lc-current-pill {
  margin-left: auto;
  min-width: 132px;
  padding: 0.65rem 0.9rem;
  border-radius: 14px;
  background: linear-gradient(145deg, #0f766e, #0e7490);
  color: #fff;
  text-align: center;
  box-shadow: 0 10px 24px rgba(15, 118, 110, 0.28);
}
.lc-current-label {
  display: block;
  font-size: 0.68rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.85;
}
.lc-current-value {
  display: block;
  font-size: 1.45rem;
  line-height: 1.2;
  font-weight: 800;
}
.lc-body {
  padding: 0.5rem 1.5rem 1.5rem;
}
.lc-state,
.lc-empty {
  text-align: center;
  padding: 2.5rem 1rem;
  color: #64748b;
}
.lc-empty i {
  font-size: 2rem;
  color: #94a3b8;
}
.lc-summary {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 0.65rem;
  margin-bottom: 1.25rem;
}
.lc-chip {
  border-radius: 12px;
  padding: 0.7rem 0.8rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}
.lc-chip-label {
  display: block;
  font-size: 0.72rem;
  color: #64748b;
  margin-bottom: 0.15rem;
}
.lc-chip-value {
  font-size: 1.05rem;
  color: #0f172a;
}
.lc-timeline {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.lc-event {
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 0.85rem;
  min-height: 1px;
}
.lc-rail {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.lc-rail-dot {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: #64748b;
  box-shadow: 0 0 0 4px #fff;
  z-index: 1;
  flex-shrink: 0;
}
.lc-rail-line {
  flex: 1;
  width: 2px;
  background: linear-gradient(#cbd5e1, #e2e8f0);
  margin: 0.2rem 0 0;
  min-height: 28px;
}
.lc-card {
  margin-bottom: 0.95rem;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.92);
  padding: 0.9rem 1rem;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
}
.lc-card-top {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 0.75rem;
}
.lc-type-badge {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  padding: 0.18rem 0.5rem;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
  margin-bottom: 0.35rem;
}
.lc-card-title {
  font-size: 1rem;
  color: #0f172a;
}
.qty-in {
  color: #047857;
  font-weight: 800;
}
.qty-out {
  color: #be123c;
  font-weight: 800;
}
.lc-voucher-id {
  font-weight: 700;
  color: #334155;
  font-size: 0.9rem;
}
.lc-voucher-date {
  color: #94a3b8;
  font-size: 0.8rem;
}
.lc-facts {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.65rem;
}
.lc-fact {
  display: flex;
  gap: 0.45rem;
  align-items: flex-start;
  padding: 0.55rem 0.6rem;
  border-radius: 10px;
  background: #f8fafc;
  min-width: 0;
}
.lc-fact > i {
  color: #64748b;
  margin-top: 0.15rem;
}
.lc-fact-label {
  display: block;
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
}
.lc-fact-value {
  display: block;
  color: #1e293b;
  font-size: 0.86rem;
  word-break: break-word;
}
.lc-how-part {
  display: block;
}
.lc-how-part + .lc-how-part {
  margin-top: 0.15rem;
}

.tone-teal .lc-rail-dot,
.lc-chip.tone-teal {
  background: #0f766e;
}
.lc-chip.tone-teal {
  background: #ecfdf5;
  border-color: #99f6e4;
  color: #115e59;
}
.tone-teal .lc-type-badge {
  background: #ccfbf1;
  color: #0f766e;
}

.tone-blue .lc-rail-dot {
  background: #2563eb;
}
.lc-chip.tone-blue {
  background: #eff6ff;
  border-color: #bfdbfe;
}
.tone-blue .lc-type-badge {
  background: #dbeafe;
  color: #1d4ed8;
}

.tone-violet .lc-rail-dot {
  background: #7c3aed;
}
.lc-chip.tone-violet {
  background: #f5f3ff;
  border-color: #ddd6fe;
}
.tone-violet .lc-type-badge {
  background: #ede9fe;
  color: #6d28d9;
}

.tone-amber .lc-rail-dot {
  background: #d97706;
}
.lc-chip.tone-amber {
  background: #fffbeb;
  border-color: #fde68a;
}
.tone-amber .lc-type-badge {
  background: #fef3c7;
  color: #b45309;
}

.tone-rose .lc-rail-dot {
  background: #e11d48;
}
.lc-chip.tone-rose {
  background: #fff1f2;
  border-color: #fecdd3;
}
.tone-rose .lc-type-badge {
  background: #ffe4e6;
  color: #be123c;
}

.tone-orange .lc-rail-dot {
  background: #ea580c;
}
.lc-chip.tone-orange {
  background: #fff7ed;
  border-color: #fed7aa;
}
.tone-orange .lc-type-badge {
  background: #ffedd5;
  color: #c2410c;
}

.tone-cyan .lc-rail-dot {
  background: #0891b2;
}
.lc-chip.tone-cyan {
  background: #ecfeff;
  border-color: #a5f3fc;
}
.tone-cyan .lc-type-badge {
  background: #cffafe;
  color: #0e7490;
}

.tone-slate .lc-rail-dot,
.tone-gray .lc-rail-dot {
  background: #475569;
}
.lc-chip.tone-slate {
  background: #f8fafc;
  border-color: #cbd5e1;
}

@media (max-width: 768px) {
  .lc-header {
    flex-wrap: wrap;
  }
  .lc-current-pill {
    margin-left: 0;
    order: 3;
    width: 100%;
  }
  .lc-facts {
    grid-template-columns: 1fr;
  }
  .lc-card-top {
    flex-direction: column;
  }
  .lc-card-voucher {
    text-align: left !important;
  }
}
</style>
